import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { BehaviorSubject, forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppraisalMeetingService } from '../../../employees/appraisals/meetings/services/appraisal-meeting.service';
import { AppraisalMeetingStatus } from '../../../employees/appraisals/meetings/models';

type ViewScope = 'manager' | 'employee';

@Component({
  selector: 'app-dialog-meetings-widget',
  templateUrl: './dialog-meetings-widget.component.html',
  styleUrls: ['./dialog-meetings-widget.component.scss'],
  standalone: false
})
export class DialogMeetingsWidgetComponent implements OnInit, OnDestroy {
  @Input() businessId: number;
  @Output() scopeTabChanged = new EventEmitter<string>();

  activeScope: ViewScope = 'manager';
  isLoading = true;
  shouldShow = new BehaviorSubject(false);

  /** Whether the user has at least one meeting where they are the manager */
  hasManagerMeetings = false;
  /** Whether the user has at least one meeting where they are the employee */
  hasEmployeeMeetings = false;

  chartData: any[] = [];
  legendItems: { label: string; color: string }[] = [];

  yAxisTickFormatting = (value: number) => Number.isInteger(value) ? String(value) : '';

  private readonly STATUS_COLORS: Record<number, string> = {
    [AppraisalMeetingStatus.Draft]:         '#9CA3AF',
    [AppraisalMeetingStatus.InProgress]:    '#3B82F6',
    [AppraisalMeetingStatus.PendingReview]: '#F59E0B',
    [AppraisalMeetingStatus.Completed]:     '#22C55E',
    [AppraisalMeetingStatus.Cancelled]:     '#EF4444'
  };

  private readonly STATUS_TRANSLATION_KEYS: Record<number, string> = {
    [AppraisalMeetingStatus.Draft]:         'APPRAISAL_STATUS_PENDING_ACCESS',
    [AppraisalMeetingStatus.InProgress]:    'APPRAISAL_STATUS_PRE_MEETING',
    [AppraisalMeetingStatus.PendingReview]: 'APPRAISAL_STATUS_MEETING_ACCEPTED',
    [AppraisalMeetingStatus.Completed]:     'APPRAISAL_STATUS_MEETING_COMPLETED',
    [AppraisalMeetingStatus.Cancelled]:     'APPRAISAL_STATUS_CANCELLED_MEETING'
  };

  private readonly STATUS_ORDER = [
    AppraisalMeetingStatus.Draft,
    AppraisalMeetingStatus.InProgress,
    AppraisalMeetingStatus.PendingReview,
    AppraisalMeetingStatus.Completed,
    AppraisalMeetingStatus.Cancelled
  ];

  colorScheme = {
    domain: this.STATUS_ORDER.map(s => this.STATUS_COLORS[s])
  };

  showXAxis  = false;
  showYAxis  = true;
  gradient   = false;
  showLegend = false;

  private lastCounts: { Status: number; Count: number }[] = [];
  private subscriptions = new Subscription();

  constructor(
    private appraisalMeetingService: AppraisalMeetingService,
    private translate: TranslateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.translate.onLangChange.subscribe((_: LangChangeEvent) => {
        this.buildChartData(this.lastCounts);
      })
    );
    this.loadBothScopes();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get showManagerTab(): boolean  { return this.hasManagerMeetings; }
  get showEmployeeTab(): boolean { return this.hasEmployeeMeetings; }

  setScope(scope: ViewScope): void {
    if (this.activeScope === scope) return;
    this.activeScope = scope;
    this.scopeTabChanged.emit(this.tabForScope(scope));
    this.loadStats(scope);
  }

  onSelect(event: any): void {
    const queryParams: any = {
      tab: this.tabForScope(this.activeScope),
      viewScope: this.activeScope
    };
    if (event?.extra?.status !== undefined) {
      queryParams['status'] = String(event.extra.status);
    }
    const currentUrl = this.router.url;
    const segments = currentUrl.split('/');
    segments[segments.length - 1] = 'employee';
    segments.push('appraisals');
    this.router.navigate([segments.join('/')], { queryParams });
  }

  private tabForScope(scope: ViewScope): string {
    return scope === 'manager' ? 'list' : 'my-meetings';
  }

  private totalCount(counts: { Status: number; Count: number }[]): number {
    return counts.reduce((sum, c) => sum + c.Count, 0);
  }

  /**
   * Fetches both scopes in parallel on init to decide which tabs to show,
   * then displays chart data for whichever scope is active.
   */
  private loadBothScopes(): void {
    this.isLoading = true;
    this.shouldShow.next(false);

    const emptyResult = { StatusCounts: [] };

    this.subscriptions.add(
      forkJoin({
        manager:  this.appraisalMeetingService.getDashboardStats(this.businessId, 'manager')
                    .pipe(catchError(() => of(emptyResult))),
        employee: this.appraisalMeetingService.getDashboardStats(this.businessId, 'employee')
                    .pipe(catchError(() => of(emptyResult)))
      }).subscribe(({ manager, employee }) => {
        this.hasManagerMeetings  = this.totalCount(manager.StatusCounts  || []) > 0;
        this.hasEmployeeMeetings = this.totalCount(employee.StatusCounts || []) > 0;

        // Default to manager scope if available, otherwise employee
        this.activeScope = this.hasManagerMeetings ? 'manager' : 'employee';
        this.scopeTabChanged.emit(this.tabForScope(this.activeScope));

        const counts = this.activeScope === 'manager'
          ?manager.StatusCounts  || []
          :employee.StatusCounts || [];

        this.lastCounts = counts;
        this.buildChartData(counts);
        this.isLoading = false;
        setTimeout(() => this.shouldShow.next(true), 200);
      })
    );
  }

  private loadStats(scope: ViewScope): void {
    this.isLoading = true;
    this.shouldShow.next(false);

    this.subscriptions.add(
      this.appraisalMeetingService.getDashboardStats(this.businessId, scope)
        .subscribe({
          next: result => {
            this.lastCounts = result.StatusCounts || [];
            this.buildChartData(this.lastCounts);
            this.isLoading = false;
            setTimeout(() => this.shouldShow.next(true), 200);
          },
          error: () => {
            this.lastCounts = [];
            this.chartData  = [];
            this.legendItems = [];
            this.isLoading  = false;
            setTimeout(() => this.shouldShow.next(true), 200);
          }
        })
    );
  }

  private buildChartData(counts: { Status: number; Count: number }[]): void {
    const countMap = new Map(counts.map(c => [c.Status, c.Count]));

    forkJoin(
      this.STATUS_ORDER.map(s => this.translate.get(this.STATUS_TRANSLATION_KEYS[s]))
    ).subscribe(labels => {
      this.chartData = this.STATUS_ORDER.map((status, i) => ({
        name:  labels[i],
        value: countMap.get(status) ?? 0,
        extra: { status }
      }));
      this.legendItems = this.STATUS_ORDER.map((status, i) => ({
        label: labels[i],
        color: this.STATUS_COLORS[status]
      }));
    });
  }
}
